/**
 * useProjectionLink — TanStack hook chain for the Workspace ↔
 * Environment investigation deep-link bridge.
 *
 * The flow:
 *   1. Caller passes an `investigationId` and the hook mints (or
 *      retrieves from cache) an opaque projection token via POST.
 *   2. The hook then resolves the token via GET, returning either
 *      the full projection summary OR a redacted sentinel.
 *   3. Tokens have a 1h server-side TTL; the hook re-mints on
 *      refetch, so URL-history token leaks are bounded by the
 *      same TTL.
 *
 * Cycle-102 sprint-3 Task 3.1.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createProjectionLink,
  resolveProjectionLink,
  type ProjectionLinkResponse,
  type ProjectionResolveResponse,
} from '../../../api/projectionClient';

// Re-mint slightly before the 1h TTL so the resolve never races a
// just-expired token. 55 minutes lines up with the SDD §1.4.5 budget.
const PROJECTION_REFRESH_MS = 55 * 60_000;

interface UseProjectionLinkOptions {
  enabled?: boolean;
}

export function useProjectionLink(
  investigationId: string | null,
  options: UseProjectionLinkOptions = {},
) {
  const enabled = (options.enabled ?? true) && Boolean(investigationId);

  const linkQuery = useQuery({
    queryKey: ['projection', 'link', investigationId],
    queryFn: () => createProjectionLink(investigationId as string),
    enabled,
    staleTime: PROJECTION_REFRESH_MS,
    refetchInterval: PROJECTION_REFRESH_MS,
  });

  const token = linkQuery.data?.token ?? null;

  const resolveQuery = useQuery({
    queryKey: ['projection', 'resolve', token],
    queryFn: () => resolveProjectionLink(token as string),
    enabled: enabled && Boolean(token),
    staleTime: PROJECTION_REFRESH_MS,
    refetchInterval: PROJECTION_REFRESH_MS,
  });

  return {
    isLoading: linkQuery.isLoading || resolveQuery.isLoading,
    error: linkQuery.error ?? resolveQuery.error,
    link: linkQuery.data ?? null,
    projection: resolveQuery.data ?? null,
    refetch: async () => {
      await linkQuery.refetch();
      await resolveQuery.refetch();
    },
  };
}

/**
 * Imperative variant — used by the Workspace-side double-click handler
 * that wants a fresh token on demand without subscribing to a query.
 */
export function useMintProjectionLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (investigationId: string) => createProjectionLink(investigationId),
    onSuccess: (data: ProjectionLinkResponse, investigationId) => {
      qc.setQueryData(['projection', 'link', investigationId], data);
    },
  });
}

export type ProjectionLinkResult = {
  isLoading: boolean;
  error: unknown;
  link: ProjectionLinkResponse | null;
  projection: ProjectionResolveResponse | null;
};
